import axios from 'axios';
import { storageService } from './storageService';

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: 'https://api.helexpo.gr/mobilev2/api/client/',
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_USETOKEN}`,
        'Content-Type': 'application/json',
      },
      timeout: 15000,
    });

    this.scanClient = axios.create({
      baseURL: 'https://api.helexpo.gr/scan/api/',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 15000,
    });

    this.setupInterceptors();
    this.loadingStates = new Map();

    // Promises for requests currently on the wire, keyed by cache key, so two
    // components mounting at once share one response instead of racing.
    this.inFlight = new Map();

    // The list endpoints are large (the exhibitors payload is ~800KB) and the
    // venue network is congested, so they get a longer budget than the 15s
    // default used for small requests.
    this.listTimeout = 30000;
  }

  setupInterceptors() {
    [this.client, this.scanClient].forEach(client => {
      client.interceptors.request.use(
        (config) => {
          const requestId = `${config.method?.toUpperCase()}_${config.url}`;
          this.loadingStates.set(requestId, true);
          return config;
        },
        (error) => Promise.reject(error)
      );

      client.interceptors.response.use(
        (response) => {
          const requestId = `${response.config.method?.toUpperCase()}_${response.config.url}`;
          this.loadingStates.set(requestId, false);
          return response;
        },
        (error) => {
          const requestId = error.config ? `${error.config.method?.toUpperCase()}_${error.config.url}` : 'UNKNOWN';
          this.loadingStates.set(requestId, false);
          
          if (error.response?.status === 401) {
            this.handleUnauthorized();
          }
          
          return Promise.reject(error);
        }
      );
    });
  }

  handleUnauthorized() {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('userData_') || key.includes('auth') || key.includes('token'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    }
  }

  storage = {
    setItem: (key, value) => {
      try {
        const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
        localStorage.setItem(key, serializedValue);
        return true;
      } catch (error) {
        console.error('Storage set error:', error);
        return false;
      }
    },
    
    getItem: (key, defaultValue = null) => {
      try {
        const item = localStorage.getItem(key);
        if (item === null) return defaultValue;
        
        try {
          return JSON.parse(item);
        } catch {
          return item;
        }
      } catch (error) {
        console.error('Storage get error:', error);
        return defaultValue;
      }
    },
    
    removeItem: (key) => {
      try {
        localStorage.removeItem(key);
        return true;
      } catch (error) {
        console.error('Storage remove error:', error);
        return false;
      }
    }
  };

  // ============================================================
  // Network resilience helpers
  // ============================================================

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // A dropped connection, a timeout, or a server-side hiccup is worth another
  // go. A 4xx (bad id, unauthorized) will fail identically every time.
  isRetriableError(error) {
    if (!error?.response) return true; // timeout / offline / DNS / reset
    const status = error.response.status;
    return status === 408 || status === 429 || status >= 500;
  }

  async requestWithRetry(doRequest, { retries = 2, baseDelay = 700, label = 'request' } = {}) {
    let lastError;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await doRequest();
      } catch (error) {
        lastError = error;
        if (attempt === retries || !this.isRetriableError(error)) break;

        const wait = baseDelay * Math.pow(2, attempt);
        console.warn(`🔁 Retrying ${label} in ${wait}ms (attempt ${attempt + 2}/${retries + 1}):`, error.message);
        await this.delay(wait);
      }
    }

    throw lastError;
  }

  // Guard against responses that are technically HTTP 200 but carry no usable
  // data: the API's own { err, msg } envelope, and captive-portal / proxy HTML
  // pages. Both previously slipped through as an empty list.
  assertValidPayload(response, what) {
    const contentType = response?.headers?.['content-type'] || '';
    if (contentType.includes('text/html') || typeof response?.data === 'string') {
      throw new Error(`Invalid response received while loading ${what}. Check your network connection.`);
    }

    const payload = response?.data;
    if (payload && payload.err !== undefined && payload.err !== null && payload.err !== 0) {
      throw new Error(payload.msg || `Failed to fetch ${what}`);
    }
  }

  /**
   * Fetch-through-cache used by every list endpoint.
   *
   * 1. fresh cache hit  -> return immediately
   * 2. identical request already in flight -> reuse it
   * 3. network, with retry/backoff on transient failures
   * 4. network exhausted -> fall back to expired cache rather than error out
   */
  async cachedRequest(cacheKey, fetcher, options = {}) {
    const {
      ttlMinutes = 59,
      retries = 2,
      label = cacheKey,
      allowStaleFallback = true
    } = options;

    // Non-destructive read: an expired entry is kept on disk so it can still
    // serve as the fallback below if the network request fails.
    const entry = storageService.getCacheEntry(cacheKey);
    if (entry && !entry.expired) return entry.data;

    if (this.inFlight.has(cacheKey)) return this.inFlight.get(cacheKey);

    const request = (async () => {
      try {
        const data = await this.requestWithRetry(fetcher, { retries, label });

        // Never cache an empty result — an empty list is far more likely to be
        // a transient upstream problem than a real answer, and caching it would
        // pin the failure in place for the next 59 minutes.
        const isEmpty = data === null || data === undefined ||
          (Array.isArray(data) && data.length === 0);
        if (!isEmpty) storageService.setCacheData(cacheKey, data, ttlMinutes);

        return data;
      } catch (error) {
        if (allowStaleFallback && entry) {
          console.warn(`⚠️ ${label} request failed, serving stale cache:`, error.message);
          return entry.data;
        }
        throw error;
      } finally {
        this.inFlight.delete(cacheKey);
      }
    })();

    this.inFlight.set(cacheKey, request);
    return request;
  }

  // Turn an axios failure into something a user can act on.
  describeError(error, fallback) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return 'You appear to be offline. Please check your connection and try again.';
    }
    if (error?.code === 'ECONNABORTED') {
      return 'The request took too long. Please try again.';
    }
    if (error?.response?.status >= 500) {
      return 'Server error. Please try again later.';
    }
    if (!error?.response && error?.message === 'Network Error') {
      return 'Could not reach the server. Please check your connection and try again.';
    }
    return fallback;
  }

 async getExhibitions(userId = "60b9a686bba90d258aa8e204ab7f91f0") {
  try {
    // // Use consistent cache key format like other methods
     return await this.cachedRequest(`exhibitions_${userId}`, async () => {
    const response = await this.client.get(`protected/exhibitions/${userId}`, {
      timeout: this.listTimeout
    });

    this.assertValidPayload(response, 'exhibitions');

    if (!response.data?.results) return [];

    const exhibitions = response.data.results.map((exhibition) => ({
      value: exhibition.ExCode,
      label: exhibition.Title,
      labelEng: exhibition.TitleEng,
      Descr: exhibition.Descr,
      DescrEng: exhibition.DescrEng,
      bannerUrl: exhibition.Logo,
      VATNeeded: exhibition.VATNeeded,
      CompanyActivitiesListNeeded: exhibition.CompanyActivitiesListNeeded,
      JobListNeeded: exhibition.JobListNeeded,
      RegistrationMsgGr: exhibition.RegistrationMsgGr,
      RegistrationMsgEng: exhibition.RegistrationMsgEng,
      IsRegistered: exhibition.IsRegistered || 0,
      BarCode: exhibition.BarCode || '',
      ProfileID: exhibition.ProfileID || 0,
      HasTicket: exhibition.HasTicket,
      HasServices: exhibition.HasServices,
      TicketURL: exhibition.TicketURL,
      ServicesURL: exhibition.ServicesURL,
      hasSponsors: exhibition.HasSponsors,
      SponsorsUrl: exhibition.SponsorURL
    }));

    return exhibitions;
    }, { ttlMinutes: 60, label: 'exhibitions' });
  } catch (error) {
    console.error('Failed to fetch exhibitions:', error);
    throw new Error(this.describeError(error, 'Failed to load exhibitions. Please try again.'));
  }
}


 async getExhibitors(exhibitionId) {
  try {
    return await this.cachedRequest(`exhibitors_${exhibitionId}`, async () => {
    const response = await this.client.get(`protected/exhibitors/${exhibitionId}`, {
      timeout: this.listTimeout
    });

    this.assertValidPayload(response, 'exhibitors');

    if (!response.data?.results) return [];

    const exhibitors = response.data.results.map((company) => ({
      ActivityID: company.ActivityID,
      logoURL: company.logoURL || 'https://via.placeholder.com/150',
      CityGr: company.CityGr,
      CityEng: company.CityEng,
      EponCatGr: company.EponCatGr,
      EponCatEng: company.EponCatEng,
      CountryEng: company.CountryEng,
      CountryGr: company.CountryGr,
      StreetEng: company.StreetEng,
      ActivityIDNum: company.ActivityIDNum,
      ContactIDNum: company.ContactIDNum,
      PGR: company.PGR,
      PENG: company.PENG,
      eENG: company.eENG,
      eGR: company.eGR,
      WebSite: company.WebSite,
      RegionEng: company.RegionEng,
      RegionGr: company.RegionGr,
      StreetNum: company.StreetNum,
      BRANCH: company.BRANCH,
      StreetGr: company.StreetGr,
      MainActivityEponCatGr: company.MainActivityEponCatGr,
      MainActivityEponCatEng: company.MainActivityEponCatEng,
      ProfileGr: company.ProfileGr,
      ProfileEng: company.ProfileEng
    }));

    // The feed contains a few exact duplicate rows. ActivityID is the React
    // key for the virtual list, so duplicates have to go.
    return Array.from(
      new Map(exhibitors.map((company) => [company.ActivityID, company])).values()
    );
    }, { label: 'exhibitors' });
  } catch (error) {
    console.error('Failed to fetch exhibitors:', error);
    throw new Error(this.describeError(error, 'Failed to load exhibitors. Please try again.'));
  }
}


 async getExhibitorDetail(exhibitionId, exhibitorId) {
  try {
    // ✅ Add exhibition-specific cache key
    return await this.cachedRequest(`exhibitorDetail_${exhibitionId}_${exhibitorId}`, async () => {
      const response = await this.client.get(`protected/exhibitors/${exhibitionId}/${exhibitorId}`);

      this.assertValidPayload(response, 'exhibitor details');

      return response.data.result;
    }, { label: 'exhibitorDetail' });
  } catch (error) {
    console.error('Failed to fetch exhibitor details:', error);
    throw new Error(this.describeError(error, 'Failed to load exhibitor details. Please try again.'));
  }
}

 async getExhibits(exhibitionId) {
  try {
    return await this.cachedRequest(`exhibits_${exhibitionId}`, async () => {
    const response = await this.client.get(`protected/exhibits/${exhibitionId}`, {
      timeout: this.listTimeout
    });

    this.assertValidPayload(response, 'exhibits');

    if (!response.data?.results) return [];

    const exhibitData = response.data.results.map((exhibit) => ({
      ExhibitCode: exhibit.ExhibitCode,
      DescrGr: exhibit.DescrGr,
      DescrEng: exhibit.DescrEng,
      PavCode: exhibit.PavCode,
      PavDescrGr: exhibit.PavDescrGr,
      PavDescrEng: exhibit.PavDescrEng,
      ExCode: exhibit.ExCode,
      ActivityID: exhibit.ActivityID,
      ExhibitCodeEnc: exhibit.ExhibitCodeEnc
    }));

    const exhibits = Array.from(
      new Map(exhibitData.map((item) => [item.DescrGr, item])).values()
    );

    return exhibits;
    }, { label: 'exhibits' });
  } catch (error) {
    console.error('Failed to fetch exhibits:', error);
    throw new Error(this.describeError(error, 'Failed to load exhibits. Please try again.'));
  }
}

async getExhibitorsForExhibit(exhibitionId, exhibitId) {
  try {
    return await this.cachedRequest(`exhibitorsForExhibit_${exhibitionId}_${exhibitId}`, async () => {
      const [exhibitsResponse, companiesResponse] = await Promise.all([
        this.client.get(`protected/exhibits/pavillions/${exhibitionId}/${exhibitId}`, { timeout: this.listTimeout }),
        this.client.get(`protected/exhibits/exhibitors/${exhibitionId}/${exhibitId}`, { timeout: this.listTimeout })
      ]);

      this.assertValidPayload(exhibitsResponse, 'exhibit pavilions');
      this.assertValidPayload(companiesResponse, 'exhibit exhibitors');

      return {
        exhibits: exhibitsResponse.data?.results || [],
        companies: companiesResponse.data?.results || []
      };
    }, { label: 'exhibitorsForExhibit' });
  } catch (error) {
    console.error('Failed to fetch exhibit details:', error);
    throw new Error(this.describeError(error, 'Failed to load exhibit details. Please try again.'));
  }
}

  // Pavilion APIs
  async getPavilions(exhibitionId) {
  try {
    return await this.cachedRequest(`pavilions_${exhibitionId}`, async () => {
      const response = await this.client.get(`protected/pavillions/${exhibitionId}`, {
        timeout: this.listTimeout
      });

      this.assertValidPayload(response, 'pavilions');

      if (!response.data?.results) return [];

      return response.data.results.map((pavilion) => ({
        PavCode: pavilion.PavCode,
        PavDescrGr: pavilion.PavDescrGr,
        PavDescrEng: pavilion.PavDescrEng,
      }));
    }, { label: 'pavilions' });
  } catch (error) {
    console.error('Failed to fetch pavilions:', error);
    throw new Error(this.describeError(error, 'Failed to load pavilions. Please try again.'));
  }
}

async getExhibitionInfo(exhibitionId) {
  try {
    // ✅ This is already correct - exhibition-specific cache key
    return await this.cachedRequest(`info_${exhibitionId}`, async () => {
    const response = await this.client.get(`protected/info/${exhibitionId}`);

    this.assertValidPayload(response, 'exhibition info');

    if (!response.data?.results || !Array.isArray(response.data.results) || response.data.results.length === 0) {
      return null;
    }

    const infoResult = response.data.results[0];

    const infoData = {
      ExCode: infoResult.ExCode || exhibitionId,
      infoTextGr: infoResult.infoTextGr || '',
      infoTextEng: infoResult.infoTextEng || '',
      lastUpdated: new Date().toISOString()
    };

    //console.log('📋 Loaded exhibition info for:', exhibitionId);
    return infoData;
    }, { label: 'exhibitionInfo' });
  } catch (error) {
    console.error('Failed to fetch exhibition info:', error);

    if (error.response?.status === 404) {
      throw new Error('Exhibition information not found.');
    }
    throw new Error(this.describeError(error, 'Failed to load exhibition information. Please try again.'));
  }
}


async getExhibitionEvents(exhibitionId) {
  try {
    // ✅ This is already correct - exhibition-specific cache key
    return await this.cachedRequest(`events_${exhibitionId}`, async () => {
    const response = await this.client.get(`protected/events/${exhibitionId}`, {
      timeout: this.listTimeout
    });

    this.assertValidPayload(response, 'events');

    if (!response.data?.results || !Array.isArray(response.data.results)) {
      return [];
    }

    const events = response.data.results.map((event) => {
      const cleanString = (str) => str ? str.trim() : '';
      
      return {
        EventID: event.EventID,
        EventUUID: event.EventUUID || '',
        titleGr: cleanString(event.titleGr),
        titleEng: cleanString(event.titleEng),
        descriptionGr: cleanString(event.descriptionGr),
        descriptionEng: cleanString(event.descriptionEng),
        date: cleanString(event.date),
        TimeFrom: cleanString(event.TimeFrom),
        TimeTo: cleanString(event.TimeTo),
        venueGr: cleanString(event.venueGr),
        VenueEng: cleanString(event.VenueEng),
        Important: event.Important || 0,
        ExCode: event.ExCode,
        OrganizerName: cleanString(event.OrganizerName),
        OrganizerNameEng: cleanString(event.OrganizerNameEng),
        PDFENG: event.PDFENG || '',
        PDFGR: event.PDFGR || '',
        
        // Add derived fields for easier use
        startDate: cleanString(event.date),
        startTime: cleanString(event.TimeFrom),
        endTime: cleanString(event.TimeTo),
        isFeatured: event.Important === 1,
        isActive: true 
      };
    });

    // Sort events by date and time
    events.sort((a, b) => {
      const dateA = new Date(a.date.trim() + ' ' + (a.TimeFrom.trim() || '00:00'));
      const dateB = new Date(b.date.trim() + ' ' + (b.TimeFrom.trim() || '00:00'));
      return dateA - dateB;
    });

    //console.log(`📅 Loaded ${events.length} events for exhibition ${exhibitionId}`);

    return events;
    }, { label: 'events' });
  } catch (error) {
    console.error('Failed to fetch exhibition events:', error);

    if (error.response?.status === 404) {
      throw new Error('No events found for this exhibition.');
    }
    throw new Error(this.describeError(error, 'Failed to load events. Please try again.'));
  }
}

 async getExhibitsByPavilion(pavilionId, exhibitionId) {
  try {
    return await this.cachedRequest(`exhibitsByPavilion_${exhibitionId}_${pavilionId}`, async () => {
      const response = await this.client.get(`protected/pavillions/exhibits/${exhibitionId}/${pavilionId}`, {
        timeout: this.listTimeout
      });

      this.assertValidPayload(response, 'pavilion exhibits');

      return response.data.results || [];
    }, { label: 'exhibitsByPavilion' });
  } catch (error) {
    console.error('Failed to fetch pavilion exhibits:', error);
    throw new Error(this.describeError(error, 'Failed to load pavilion exhibits. Please try again.'));
  }
}

 async getExhibitorsByPavilion(pavilionId, exhibitionId) {
  try {
    return await this.cachedRequest(`exhibitorsByPavilion_${exhibitionId}_${pavilionId}`, async () => {
      const response = await this.client.get(`protected/pavillions/exhibitors/${exhibitionId}/${pavilionId}`, {
        timeout: this.listTimeout
      });

      this.assertValidPayload(response, 'pavilion exhibitors');

      return response.data.results || [];
    }, { label: 'exhibitorsByPavilion' });
  } catch (error) {
    console.error('Failed to fetch pavilion exhibitors:', error);
    throw new Error(this.describeError(error, 'Failed to load pavilion exhibitors. Please try again.'));
  }
}

  async loginExhibitor(username, password, exhibitionId) {
    try {
      const response = await this.scanClient.post('login/e', {
        un: username,
        pwd: password,
      }, {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_USETOKEN}`,
        }
      });
      
      if (response.data?.token && exhibitionId) {
        // Use storage service instead of direct localStorage
        storageService.setExhibitionUserData(exhibitionId, response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to login exhibitor:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Invalid username or password. Please try again.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error('Login failed. Please check your connection and try again.');
      }
    }
  }
  async registerUser(data) {
    try {
      const response = await this.client.post('register', data, {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_REGISTERTOKEN}`,
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to register user:', error);
      
      if (error.response?.status === 400) {
        throw new Error('Invalid registration data. Please check your inputs.');
      } else if (error.response?.status === 409) {
        throw new Error('User already registered. Please try logging in instead.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error('Registration failed. Please check your connection and try again.');
      }
    }
  }

  async verifyOTP(data) {
    try {
      const response = await this.client.post('register/OTP', data, {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_REGISTERTOKEN}`,
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to verify OTP:', error);
      throw new Error('OTP verification failed. Please try again.');
    }
  }

  async loginUser(credentials) {
    try {
      const response = await this.client.post('login', credentials);
      return response.data;
    } catch (error) {
      console.error('Failed to login user:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Invalid credentials. Please try again.');
      } else {
        throw new Error('Login failed. Please try again.');
      }
    }
  }

  // Visitor APIs
  async registerVisitor(data) {
    try {
      const response = await this.client.post('protected/visitors', data);
      return response.data;
    } catch (error) {
      console.error('Failed to register visitor:', error);
      
      if (error.response?.status === 400) {
        throw new Error('Invalid registration data. Please check your inputs.');
      } else if (error.response?.status === 409) {
        throw new Error('Visitor already registered. Please try logging in instead.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error('Registration failed. Please check your connection and try again.');
      }
    }
  }

  async getCountries() {
    try {
      const response = await this.client.get('protected/visitors/countries');
      return response.data?.results || [];
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      throw new Error('Failed to load countries. Please try again.');
    }
  }

  async getJobs(exhibitionId) {
    try {
      const response = await this.client.get(`protected/visitors/visitorsjob/${exhibitionId}`);
      return response.data?.results || [];
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      throw new Error('Failed to load job positions. Please try again.');
    }
  }

  async getCompanyActivities(exhibitionId) {
    try {
      const response = await this.client.get(`protected/visitors/companyactivities/${exhibitionId}`);
      return response.data?.results || [];
    } catch (error) {
      console.error('Failed to fetch company activities:', error);
      throw new Error('Failed to load company activities. Please try again.');
    }
  }

  // QR Scanner APIs
  async scanQR(barcode, token) {
    try {
      const response = await this.scanClient.post('scan', 
        { barcode },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to scan QR code:', error);
      throw new Error('Failed to scan QR code. Please try again.');
    }
  }

  async addContact(contactData, token) {
    try {
      const response = await this.scanClient.post('addcontact', 
        contactData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (response.data.err === 0) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to add contact');
      }
    } catch (error) {
      console.error('Failed to add contact:', error);
      throw new Error('Failed to add contact. Please try again.');
    }
  }

  // Utility methods
  clearExhibitionCache(exhibitionId) {
  const keysToRemove = [
    `exhibitors_${exhibitionId}`,
    `exhibits_${exhibitionId}`,
    `info_${exhibitionId}`,
    `events_${exhibitionId}`,
    `pavilions_${exhibitionId}` // if you have this
  ];
  
  keysToRemove.forEach(key => {
    storageService.getCacheData(key) && storageService.removeItem(`cache_${key}`);
  });
  
  console.log(`🧹 Cleared cache for exhibition: ${exhibitionId}`);
}

// Add this method to force refresh data for current exhibition
async refreshExhibitionData(exhibitionId) {
  // Clear the cache first
  this.clearExhibitionCache(exhibitionId);
  
  // Then reload the data
  try {
    const [exhibitors, exhibits, info, events] = await Promise.allSettled([
      this.getExhibitors(exhibitionId),
      this.getExhibits(exhibitionId), 
      this.getExhibitionInfo(exhibitionId),
      this.getExhibitionEvents(exhibitionId)
    ]);
    
    //console.log('✅ Refreshed all data for exhibition:', exhibitionId);
    return { exhibitors, exhibits, info, events };
  } catch (error) {
    //console.error('❌ Failed to refresh exhibition data:', error);
    throw error;
  }
}
  isLoggedInToExhibition(exhibitionId) {
    if (!exhibitionId) return false;
    
    try {
      const userData = storageService.getExhibitionUserData(exhibitionId);
      return !!userData && !!userData.token;
    } catch (error) {
      return false;
    }
  }
async sendUserIdentification(identificationData) {
  try {
    const response = await this.client.post('deviceregistration/pwa', identificationData);
    return response.data;
  } catch (error) {
    console.error('Failed to send user identification:', error);
    throw new Error('Failed to send user identification data.');
  }
}

 getExhibitionUserData(exhibitionId) {
    if (!exhibitionId) return null;
    return storageService.getExhibitionUserData(exhibitionId);
  }

  getAllLoggedInExhibitions() {
    return storageService.getAllLoggedInExhibitions();
  }
  logoutFromExhibition(exhibitionId) {
    if (!exhibitionId) return false;
    return storageService.removeExhibitionUserData(exhibitionId);
  }

  getLoadingState(method, url) {
    const requestId = `${method.toUpperCase()}_${url}`;
    return this.loadingStates.get(requestId) || false;
  }

  clearAllLoadingStates() {
    this.loadingStates.clear();
  }

  cancelToken() {
    return axios.CancelToken.source();
  }

  clearUserCache() {
    storageService.clearAllCaches();
    // console.log('🧹 User cache cleared');
  }


  isCancel(error) {
    return axios.isCancel(error);
  }
}


export const apiService = new ApiService();
export { ApiService };

export const API_ERRORS = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  SERVER_ERROR: 500,
  NETWORK_ERROR: 'NETWORK_ERROR'
};

export default apiService;